import { call, cancel, select, take, takeEvery } from 'redux-saga/effects';
import {
  GlobalAction,
  GlobalActions,
  GlobalEventDispatcher,
  GlobalPut,
  ModalCloseReason,
  ModalType,
  SpeichernStatus,
} from '@ps20-frontend/shop-integration-redux';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { App } from '@ps20-frontend/frontend-models';
import { IAppState } from '@ps20-frontend/apps/dashboard-store';
import { DashboardActions } from '../dashboard.actions';
import { selectIsUnderwriterInfoComplete } from '../selectors/warenkorb.selector';
import { refuseWhileShopPricingIsStale } from './helper-functions/shopPricingStaleGuard';

export function* requestForwardToUnderwriterSaga() {
  yield takeEvery(
    DashboardActions.REQUEST_FORWARD_TO_UNDERWRITER,
    safeSaga(openForwardToUnderwriterModal, App.SHOP_DASHBOARD),
  );
}

export function* openForwardToUnderwriterModal() {
  const state: IAppState = yield select();
  if (!selectIsUnderwriterInfoComplete(state)) {
    yield cancel();
  }

  const globalActions = new GlobalActions();
  const dashboardActions = new DashboardActions();

  if (yield* refuseWhileShopPricingIsStale(state)) {
    return;
  }

  yield call(
    GlobalPut.getInstance().put,
    globalActions.requestModal(ModalType.FORWARD_TO_UNDERWRITER),
  );
  const closeModalAction: GlobalAction<ModalCloseReason> = yield take(GlobalActions.CLOSE_MODAL);

  if (closeModalAction.payload === ModalCloseReason.PRIMARY) {
    const confirmedState: IAppState = yield select();
    if (yield* refuseWhileShopPricingIsStale(confirmedState)) {
      return;
    }

    try {
      GlobalEventDispatcher.getInstance().dispatch(dashboardActions.forwardToUnderwriterStarted());

      const shopState = GlobalEventDispatcher.getInstance().getStateByAngebot(App.SHOP);
      const cryptId = shopState.transientState.datenhaltung.av.cryptId;
      const isNewOffer = !cryptId;

      if (isNewOffer) {
        yield call(GlobalPut.getInstance().put, globalActions.speichernRequested());
        const firstSaveStatus: GlobalAction<SpeichernStatus> = yield take(
          GlobalActions.SAVE_STATUS_RECEIVED,
        );

        if (firstSaveStatus.payload.status !== 200) {
          yield call(
            GlobalPut.getInstance().put,
            globalActions.requestModal(ModalType.FORWARD_TO_UNDERWRITER_ERROR, {
              body: 'Beim Speichern des Angebots ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
              button: 'Schließen',
            }),
          );
          return;
        }

        const savedState: IAppState = yield select();
        if (yield* refuseWhileShopPricingIsStale(savedState)) {
          return;
        }

        yield call(GlobalPut.getInstance().put, globalActions.speichernRequested(true));
        const secondSaveStatus: GlobalAction<SpeichernStatus> = yield take(
          GlobalActions.SAVE_STATUS_RECEIVED,
        );

        if (secondSaveStatus.payload.status !== 200) {
          yield call(
            GlobalPut.getInstance().put,
            globalActions.requestModal(ModalType.FORWARD_TO_UNDERWRITER_ERROR, {
              body: 'Beim Weiterleiten an den Underwriter ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
              button: 'Schließen',
            }),
          );
          return;
        }
      } else {
        yield call(GlobalPut.getInstance().put, globalActions.speichernRequested(true));
        const saveStatus: GlobalAction<SpeichernStatus> = yield take(
          GlobalActions.SAVE_STATUS_RECEIVED,
        );

        if (saveStatus.payload.status !== 200) {
          yield call(
            GlobalPut.getInstance().put,
            globalActions.requestModal(ModalType.FORWARD_TO_UNDERWRITER_ERROR, {
              body: 'Beim Weiterleiten an den Underwriter ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
              button: 'Schließen',
            }),
          );
          return;
        }
      }
      yield call(GlobalPut.getInstance().put, globalActions.openForwardedToUnderwriterPage());

    } finally {
      GlobalEventDispatcher.getInstance().dispatch(dashboardActions.forwardToUnderwriterFinished());
    }
  }
}
